<template>
  <div class="is-size-5">
    <form-header />
    <form
      class="form"
      @change="setPageNavRequiresConfirmation(true)"
    >
      <div class="form-section">
        <custom-input
          label="Account domain"
          :value="websiteDetails && websiteDetails.domainName"
          readonly
        />
      </div>

      <!-- Instruction to client on how to use the form -->
      <div class="form-section form-instruction dark">
        <div>{{ $t('modify_website.form.instruction') }}</div>
      </div>

      <transition-group
        name="list"
        tag="div"
      >
        <!-- Page section. There can be multiple page sections, one for each page that the customer
          wishes to modify -->
        <div
          v-for="(page, index) in $v.pages.$each.$iter"
          :key="pages[index].identifier"
          class="form-section is-clearfix"
        >
          <!-- The name of the web page to be modified -->
          <custom-input
            :label="$t('modify_website.form.field2.label')"
            :error-text="errorMsg(page.name)"
          >
            <auto-complete
              :id="`page-${pages[index].identifier}`"
              v-model.lazy="page.name.$model"
              :disabled="submitted"
              :placeholder="$t('modify_website.form.field2.placeholder')"
              :input-class="`input is-size-4 ${errorMsg(page.name) ? 'is-danger shake' : ''}`"
              :data="websiteDetails.pages.map(page => page.name)"
              @input="(_, disableValidation) => delayTouch(page.name, disableValidation)"
            />
          </custom-input>
          <!-- end of 'name' input section -->

          <custom-input
            :label="$t('modify_website.form.field3.label')"
            :info-text="$t('modify_website.form.field3.description')"
            :error-text="errorMsg(page.change)"
          >
            <textarea
              :id="`change-${pages[index].identifier}`"
              v-model="page.change.$model"
              :class="`textarea is-size-4 ${errorMsg(page.change) ? 'is-danger shake' : ''}`"
              :disabled="submitted"
              :placeholder="$t('modify_website.form.field3.placeholder')"
              :rows="3"
              @input="(_, disableValidation) => delayTouch(page.change, disableValidation)"
            />
          </custom-input>
          <!-- end of 'changes' input section -->

          <!-- Any files the client wishes to upload -->
          <custom-input
            :label="$t('modify_website.form.field4.label')"
            :warning-text="pages[index].warning"
            :info-text="$t('modify_website.form.field4.description')"
            :label-as-a-container="false"
          >
            <file-uploader
              v-model="pages[index].files"
              :submitted="submitted"
              :identifier="pages[index].identifier"
              @uploading="(isUploading) => loading = isUploading"
              @warning="(msg, consoleMsg) => flashWarningUnderField(index, msg, consoleMsg)"
            />
          </custom-input>
          <!-- end of 'file' upload section -->

          <div>
            <button
              class="button is-danger is-large is-broad is-uppercase is-pulled-right"
              :style="{visibility: pages.length > 1 ? 'visible' : 'hidden' }"
              @click.prevent="deletePage(pages[index].identifier)"
            >
              {{ $t('modify_website.buttons.deletePage') }}
            </button>
          </div>
        </div>
        <!-- end of page section -->
      </transition-group>
      <div class="form-section">
        <button
          class="button is-primary is-large is-broad is-uppercase"
          @click.prevent="addPage(Number(pages.length - 1), pages[pages.length - 1].identifier)"
        >
          {{ $t('modify_website.buttons.addPage') }}
        </button>
      </div>
      <div class="columns form-section dark">
        <div
          class="column is-5"
        >
          <button
            class="button is-primary is-large is-broad is-uppercase"
            type="submit"
            :disabled="loading || submitted"
            @click.prevent="handleSubmit($v)"
          >
            {{ $t('modify_website.buttons.change') }}
          </button>
        </div>

        <div class="column">
          <i18n
            path="modify_website.privacy_policy"
          >
            <a
              slot="policy_link"
              class="is-text-color-info"
              :style="{color: '#b5d1ff'}"
              :href="$t('modify_website.policy_url')"
              target="_blank"
            >
              {{ $t('modify_website.policy_link') }}
            </a>
          </i18n>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import CustomInput from '@/components/Shared/CustomInput'
import FormHeader from '@/components/PageModification/FormHeader'
import { required, minLength } from 'vuelidate/lib/validators'
import AutoComplete from '@/components/Shared/AutoComplete'
import FileUploader from '@/components/Shared/FileUploader'
export default {

  name: 'ModFormPresentation',

  components: {
    CustomInput,
    FormHeader,
    FileUploader,
    AutoComplete
  },

  validations () {
    return {
      pages: {
        $each: {
          name: {
            required,
            minLength: minLength(2)
          },
          change: {
            required,
            minLength: minLength(10)
          }
        }
      }
    }
  },

  props: {
    websiteDetails: {
      type: Object,
      default: () => ({})
    },
    pages: {
      type: Array,
      default: () => ([])
    },
    // Indicate whether the form has been submitted
    submitted: {
      type: Boolean,
      default: false
    },
    // Sets a value in the Vuex store to ensure that any movement from the current page
    // will have to be confirmed to avoid losing information
    setPageNavRequiresConfirmation: {
      type: Function,
      default: () => {}
    },
    // Will add another section on the form for another page that requires modification
    addPage: {
      type: Function,
      default: () => {}
    },
    // Will remove the page modification that was clicked on
    deletePage: {
      type: Function,
      default: () => {}
    },
    // determine the error message to show depending on the result of the validation of a field
    errorMsg: {
      type: Function,
      default: () => {}
    },
    // Adds a delay between a keypress and the validation process to give the user a chance to
    // finish typing before they are alerted to validation errors of the input field
    delayTouch: {
      type: Function,
      default: () => {}
    },
    // Handles the logic of submitting the form
    handleSubmit: {
      type: Function,
      default: () => {}
    }
  },
  data () {
    return {
      // Indicates whether a file is currently uploading to S3
      loading: false
    }
  },

  beforeDestroy () {
    clearTimeout(this.timer)
  },

  methods: {
    // @vuese
    // Control the warnings that appear under the input, and when they disappear
    flashWarningUnderField (index, text, consoleMessage) {
      this.pages[index].warning = text
      this.timer = setTimeout(() => { this.$delete(this.pages[index], 'warning') }, 3000) /* SECONDS_IN_MS(4) */
      if (consoleMessage) {
        console.warn(consoleMessage)
      }
    }
  }

}
</script>

<style lang="scss" scoped>
@import '../../../sass/_variables.scss';
.list-item {
  display: inline-block;
  opacity: 1;
}

.list-enter-active, .list-leave-active {
  transition: all 1s;
}

.list-enter, .list-leave-to {
  opacity: 0;
  transition: all .7s;
}

.loading {
  opacity: .4;
}

.form {
  box-shadow: none;
  margin-top: 0;

  .form-section {
    background-color: var(--form-color-light);
    color: black;

    &.form-instruction {
      font-size: 1.2em;
    }

    &.dark {
      background-color: var(--form-color-dark);
      color: white;
    }
  }
}

.domain-name-title {
  margin-bottom: 0;
  text-align: center;
}

.domain-name-textbox {
  background-color: rgba(255,255,255,.4);
  padding: 4px 10px;
}

@media only screen and (min-width: 800px) {
  .form .form-section {
    padding: 20px 50px;
  }
}

@media only screen and (min-width: 1200px) {
  .form .form-section {
    padding: 20px 150px;
  }
}

</style>

<style type="text/css">
  /* needs to apply globally */
  .error-border {
    border-color: red !important;
  }
  .shake {
    animation-duration: .4s;
    animation-iteration-count: 1;
    animation-name: shake !important;
    animation-timing-function: linear;
    transform-origin: 50% 50%;
  }
</style>
